import { useNavigate, Path } from "@/router";
import FileUploadModal from "./FileUploadModal/FileUploadModal";
import { useCallback, useEffect } from "react";

// Create a proper type for the converted job data
interface ConvertedJobData {
  jobTitle?: string;
  title?: string;
  overview?: string;
  category?: string;
  facilityId?: string;
  facilityName?: string;
  compensation?: {
    baseMaximum?: number;
    baseMinimum?: number;
    totalAnticipated?: number;
  };
  benefits?: string;
  additionalIncome?: string;
  pointOfContact?: {
    name?: string;
    email?: string;
    title?: string;
    phone?: string;
  };
  employerId?: string;
  employerData?: any;
  requiredEducation?: string;
  requiredExperienceInYears?: number | string;
  requiredExperienceDescription?: string;
  essentialResponsibilities?: string;
  applicantGroup?: string;
  applyVia?: string;
  paySchedule?: string;
  positionType?: string;
  otherSections?: any[];
  otherStates?: any[];
  submissionDeadline?: {
    startDate?: string;
    endDate?: string;
  };
  additional_pga_education?: string;
  external_job_url?: string;
}

interface ConvertDocumentToJobModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  convertedJobData?: ConvertedJobData;
}

export default function ConvertDocumentToJobModal({
  open,
  onClose,
  onUpload,
  convertedJobData
}: ConvertDocumentToJobModalProps) {
  const navigate = useNavigate();

  const handleReviewJob = useCallback(async () => {
    const data = convertedJobData || {};
    try {
      const jobTitle = data.jobTitle || data.title || "";
      const category = data.category || "";

      // Format text content for consistent handling
      const formatTextContent = (text: string) => {
        if (!text) return "";

        // Clean up text - normalize line endings and remove excessive whitespace
        const cleanedText = text
          .replace(/\r\n/g, "\n") // Normalize Windows line endings
          .replace(/\r/g, "\n") // Normalize Mac line endings
          .replace(/\n{3,}/g, "\n\n") // Replace 3+ consecutive newlines with 2
          .trim();

        return cleanedText;
      };

      const transformedData = {
        name: jobTitle,
        overview: formatTextContent(data.overview || ""),
        stage: "Draft",
        serviceLevel: "Assisted",
        status: "In Progress",
        facilityId: data.facilityId || "",
        facilityName: data.facilityName || "",
        compensation: {
          baseMaximum: data.compensation?.baseMaximum || 0,
          baseMinimum: data.compensation?.baseMinimum || 0,
          totalAnticipated: data.compensation?.totalAnticipated || null,
          currencyUnit: "USD"
        },
        benefits: data.benefits || "",
        additionalIncome: data.additionalIncome || "",
        pointOfContact: {
          name: data.pointOfContact?.name || "",
          email: data.pointOfContact?.email || "",
          title: data.pointOfContact?.title || "",
          phone: data.pointOfContact?.phone || ""
        },
        employerId: data.employerId || "",
        employerData: data.employerData || null,
        category: category,
        requiredEducation: data.requiredEducation || "N/A",
        requiredExperienceInYears: String(data.requiredExperienceInYears || "0"),
        requiredExperienceDescription: data.requiredExperienceDescription || "",
        essentialResponsibilities: data.essentialResponsibilities || "",
        hideCompensationFromJobBoard: false,
        applicantGroup: data.applicantGroup || "PGA Professional and General Public",
        applyVia: data.applyVia || "PGA.org / Email Notification",
        paySchedule: data.paySchedule || "Salary",
        positionType: data.positionType || "Year Round",
        otherSections: data.otherSections || [],
        otherStates: data.otherStates || [],
        submissionDeadline: {
          startDate:
            data.submissionDeadline?.startDate ||
            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          endDate:
            data.submissionDeadline?.endDate ||
            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
        },
        additionalPgaEducation: data.additional_pga_education || "N/A",
        externalJobUrl: data.external_job_url || "",
        sendForReview: false,
        sendForReviewDescription: ""
      };

      // Saving to localStorage
      try {
        const storageData = {
          formData: transformedData,
          timestamp: Date.now()
        };
        localStorage.setItem("convertedJobData", JSON.stringify(storageData));

        // delay to await localStorage write
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Navigate to job creation page
        const currentPath = location.pathname;
        const basePath = currentPath.split("/").slice(0, -1).join("/");
        const createPath = `${basePath}/job-post/create?newJobData=true&ts=${Date.now()}`;

        // Use replace instead of navigate to ensure clean history
        navigate(createPath as Path, { replace: true });
        window.scrollTo(0, 0);
      } catch (storageError) {
        console.error("Error saving to localStorage:", storageError);
        throw new Error("Failed to save job data. Please try again.");
      }
    } catch (error) {
      console.error("Error converting document to job:", error);
      throw error;
    }
  }, [convertedJobData, navigate]);

  // Auto-navigate user to job post editor when converted data is available
  useEffect(() => {
    if (open && convertedJobData && Object.keys(convertedJobData).length > 0) {
      handleReviewJob();
    }
  }, [open, convertedJobData, handleReviewJob]);

  return (
    <FileUploadModal
      open={open}
      onClose={onClose}
      onUpload={onUpload}
      title="Convert Document to Job with PGA Assistant"
      description={
        <>
          <span>Generate a job post from an existing document.</span>
          <span className="font-extrabold text-black">
            {` Once uploaded, please review the job post and update for accuracy. `}
          </span>
          <span>This process will take a few moments.</span>
        </>
      }
      accept=".pdf,.docx,.doc,.txt"
      buttonText="Convert to Job"
    />
  );
}
